/**
 * AntiScreenshotQR — renders a QR image in a way where any single screenshot
 * (OS-level, browser, or external camera frame) is unscannable.
 *
 * How it works (temporal multiplexing):
 *   1. The source QR is drawn into an offscreen canvas at internal resolution.
 *   2. Two complementary "frames" are built using an 8×8 checker-tile mask:
 *      Frame A keeps tiles where (col+row) is even; the rest is replaced with
 *      random black/white noise. Frame B is the inverse.
 *   3. Each frame alone destroys ~50% of QR modules with noise. That blows
 *      past Reed-Solomon's max ~30% recovery → no scanner can decode either
 *      frame in isolation.
 *   4. We alternate frames at ~60fps via requestAnimationFrame. Persistence
 *      of vision in the human eye merges both frames into a clean QR.
 *   5. Any screenshot or screen-photo captures exactly one frame → unscannable.
 *   6. Noise is re-rolled every 200ms so attackers can't subtract a fixed
 *      noise pattern from a captured frame to recover the QR.
 *
 * Limits we accept honestly:
 *   - Cannot defeat a screen *recording* (60fps capture sees both frames).
 *     Screen recording is a much higher friction attack and is rare for QR
 *     copying — payment-app QR scanners scan still images, not video.
 *   - Cannot stop a high-shutter-speed external camera (1/120s+) catching
 *     one frame; but that frame is still half-noise → unscannable.
 */
import { useEffect, useRef, useState } from "react";

const TILE = 8; // px — corruption tile size; ≥ typical QR module size
const REROLL_MS = 200; // re-randomise noise this often

export default function AntiScreenshotQR({
    src,
    size = 176,
    shielded = false,
    alt = "QR",
    className = "",
}) {
    const canvasRef = useRef(null);
    // Pause the 60fps RAF + noise re-roll loop when the canvas is offscreen
    // or the tab is backgrounded. With the previous "always-on" loop, a QR
    // scrolled out of view kept burning ~5-15% CPU on mid-tier mobiles +
    // draining battery while the customer waited for their friend to scan.
    // IntersectionObserver flips this to ~0% when the QR isn't visible;
    // visibilitychange covers the "switched apps" case (e.g. customer
    // opened GPay) so the timer page stays cool in the background.
    const [active, setActive] = useState(true);

    useEffect(() => {
        const cvs = canvasRef.current;
        if (!cvs) return;
        const flip = () => {
            const tabVisible =
                typeof document === "undefined" || document.visibilityState !== "hidden";
            setActive((prev) => {
                const next = tabVisible && (cvs.__inView ?? true);
                return prev === next ? prev : next;
            });
        };
        cvs.__inView = true;
        let io = null;
        if (typeof IntersectionObserver !== "undefined") {
            io = new IntersectionObserver(
                (entries) => {
                    for (const e of entries) cvs.__inView = e.isIntersecting;
                    flip();
                },
                // 0.01 threshold = fire as soon as even 1px enters/leaves the
                // viewport. Stricter (e.g. 0.5) would keep the loop running
                // while the QR is half-scrolled, which is the exact case
                // we're trying to optimise.
                { threshold: 0.01 },
            );
            io.observe(cvs);
        }
        const onVis = () => flip();
        document.addEventListener("visibilitychange", onVis);
        return () => {
            if (io) io.disconnect();
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);

    useEffect(() => {
        const cvs = canvasRef.current;
        if (!cvs || !src) return;
        if (!active) return; // paused — don't burn CPU while offscreen / backgrounded
        let raf = null;
        let cancelled = false;
        const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
        cvs.width = size * dpr;
        cvs.height = size * dpr;
        // Note: do NOT set inline cvs.style.width/height — that would override
        // the consumer's responsive Tailwind classes (w-24 sm:w-44 etc).
        const ctx = cvs.getContext("2d");
        ctx.imageSmoothingEnabled = false;

        const img = new Image();
        // QR is rendered server-side as a data: URL → no CORS issue, but set
        // anyway in case it's ever moved to an external CDN.
        img.crossOrigin = "anonymous";
        img.onload = () => {
            if (cancelled) return;
            const innerSize = size * dpr;

            // Render the source QR at our internal canvas resolution.
            const baseCvs = document.createElement("canvas");
            baseCvs.width = innerSize;
            baseCvs.height = innerSize;
            const baseCtx = baseCvs.getContext("2d");
            baseCtx.imageSmoothingEnabled = false;
            baseCtx.drawImage(img, 0, 0, innerSize, innerSize);
            const baseData = baseCtx.getImageData(0, 0, innerSize, innerSize).data;

            const tile = TILE * dpr;
            const cols = Math.ceil(innerSize / tile);
            const rows = Math.ceil(innerSize / tile);

            // Build a complementary frame: tiles where (cx+cy)%2 === parity
            // keep the original pixels; the rest are replaced with random noise.
            const buildFrame = (parity) => {
                const out = document.createElement("canvas");
                out.width = innerSize;
                out.height = innerSize;
                const octx = out.getContext("2d");
                const id = octx.createImageData(innerSize, innerSize);
                const data = id.data;
                for (let cy = 0; cy < rows; cy++) {
                    for (let cx = 0; cx < cols; cx++) {
                        const keep = ((cx + cy) & 1) === parity;
                        const x0 = cx * tile;
                        const y0 = cy * tile;
                        const x1 = Math.min(x0 + tile, innerSize);
                        const y1 = Math.min(y0 + tile, innerSize);
                        for (let y = y0; y < y1; y++) {
                            for (let x = x0; x < x1; x++) {
                                const i = (y * innerSize + x) * 4;
                                if (keep) {
                                    data[i] = baseData[i];
                                    data[i + 1] = baseData[i + 1];
                                    data[i + 2] = baseData[i + 2];
                                    data[i + 3] = 255;
                                } else {
                                    // Random hard black/white per pixel — looks
                                    // like static; corrupts any module overlap.
                                    const v = Math.random() < 0.5 ? 0 : 255;
                                    data[i] = v;
                                    data[i + 1] = v;
                                    data[i + 2] = v;
                                    data[i + 3] = 255;
                                }
                            }
                        }
                    }
                }
                octx.putImageData(id, 0, 0);
                return out;
            };

            let frameA = buildFrame(0);
            let frameB = buildFrame(1);
            let toggle = false;
            let lastReroll = 0;

            const draw = (ts) => {
                if (cancelled) return;
                if (!lastReroll) lastReroll = ts;
                if (ts - lastReroll > REROLL_MS) {
                    frameA = buildFrame(0);
                    frameB = buildFrame(1);
                    lastReroll = ts;
                }
                ctx.clearRect(0, 0, innerSize, innerSize);
                ctx.drawImage(toggle ? frameA : frameB, 0, 0, innerSize, innerSize);
                toggle = !toggle;
                raf = requestAnimationFrame(draw);
            };
            raf = requestAnimationFrame(draw);
        };
        img.onerror = () => {
            // Fall back to a solid grey if the QR fails to load — rare path.
            ctx.fillStyle = "#e2e8f0";
            ctx.fillRect(0, 0, cvs.width, cvs.height);
        };
        img.src = src;

        return () => {
            cancelled = true;
            if (raf) cancelAnimationFrame(raf);
        };
    }, [src, size, active]);

    return (
        <canvas
            ref={canvasRef}
            data-testid="qr-canvas"
            aria-label={alt}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
            className={`qr-anti-screenshot select-none transition-all duration-200 ${shielded ? "blur-2xl scale-90" : ""} ${className}`}
            style={{
                WebkitUserSelect: "none",
                userSelect: "none",
                WebkitTouchCallout: "none",
                WebkitUserDrag: "none",
                pointerEvents: "none",
            }}
        />
    );
}
